/**
 * VPPRevenueTab — Customer-facing VPP revenue estimation component.
 *
 * Shows estimated wholesale market and DR program revenue for a project site.
 * Used across Site DNA, Flux EV, and Flux COLD dashboards via shared-ui.
 *
 * Props:
 *   project  — unified project object (location, app configs, financial results)
 *   currentUser — Firebase user object (for auth tokens)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Divider,
    IconButton,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    Slider,
    Collapse,
    Button,
} from '@mui/material';
import {
    OpenInNew as OpenInNewIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Calculate as CalculateIcon,
} from '@mui/icons-material';
import { vortxColors, vortxTypography } from '../core/theme';
import { apiPost } from '../services/api';
import { useThemeMode } from '../theme-mode/index.js';

// Currency formatter — no decimals for annual estimates
const fmtCurrency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
});

/**
 * Format a currency range string: "$X,XXX - $X,XXX"
 */
function fmtRange(low, high) {
    if (low === 0 && high === 0) return '$0';
    if (low === high) return fmtCurrency.format(low);
    return `${fmtCurrency.format(low)} \u2013 ${fmtCurrency.format(high)}`;
}

/**
 * Extract site config fields from the unified project object.
 * Searches multiple config paths for each value.
 */
function extractSiteConfig(project) {
    if (!project) return null;

    // Location
    const lat = project.location?.lat ?? project.lat;
    const lon = project.location?.lon ?? project.lon ?? project.location?.lng ?? project.lng;
    if (lat == null || lon == null) return null;

    // Battery / storage — check site_dna, flux_ev, flux_cold app configs
    const siteDna = project.apps?.site_dna ?? {};
    const fluxEv = project.apps?.flux_ev ?? {};
    const fluxCold = project.apps?.flux_cold ?? {};

    const battery_kw =
        siteDna.battery_kw ??
        siteDna.storage_kw ??
        fluxEv.battery_kw ??
        0;
    const battery_kwh =
        siteDna.battery_kwh ??
        siteDna.storage_kwh ??
        fluxEv.battery_kwh ??
        0;
    const ev_capacity_kw = fluxEv.ev_capacity_kw ?? fluxEv.total_charger_kw ?? 0;
    const controllable_load_kw =
        fluxCold.controllable_load_kw ??
        siteDna.controllable_load_kw ??
        0;

    // Existing retail savings from financial results (if available)
    const existing_retail_savings =
        project.financial_results?.annual_savings ??
        project.financial_results?.total_annual_savings ??
        siteDna.annual_savings ??
        0;

    // Utility — needed for DR program filtering
    const utility =
        project.utility ??
        project.meters?.main?.tariff?.utility ??
        '';

    return {
        lat,
        lon,
        battery_kw,
        battery_kwh,
        ev_capacity_kw,
        controllable_load_kw,
        existing_retail_savings,
        utility,
    };
}

export default function VPPRevenueTab({ project, currentUser, onEstimateChange }) {
    const { isRetro } = useThemeMode();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [estimate, setEstimate] = useState(null);
    const [participationMode, setParticipationMode] = useState('aggregator');
    const [aggregatorCutPct, setAggregatorCutPct] = useState(20);
    const [showCalcDetails, setShowCalcDetails] = useState(false);

    const fetchEstimate = useCallback(async () => {
        const siteConfig = extractSiteConfig(project);
        if (!siteConfig) {
            setError('Project is missing location data (latitude/longitude). Cannot estimate VPP revenue.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await apiPost('/api/v1/vpp/estimate', siteConfig, currentUser);
            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || `Request failed: ${response.status}`);
            }
            const data = await response.json();
            setEstimate(data);
        } catch (err) {
            setError(err.message || 'Failed to estimate VPP revenue');
        } finally {
            setLoading(false);
        }
    }, [project, currentUser]);

    useEffect(() => {
        if (project && currentUser) {
            fetchEstimate();
        }
    }, [fetchEstimate]);

    // Notify parent of estimate changes (for Deal Analysis VPP integration)
    // Uses net values (after cannibalization) so downstream consumers get accurate numbers.
    // Cannibalization is a retail-side loss (full amount), aggregator fee only applies to wholesale revenue.
    useEffect(() => {
        if (!onEstimateChange) return;
        const revFactor = participationMode === 'direct' ? 1.0 : (100 - aggregatorCutPct) / 100;
        const grossLow = estimate ? estimate.total_annual_low : 0;
        const grossHigh = estimate ? estimate.total_annual_high : 0;
        const cannibLow = estimate?.cannibalization?.total_lost_savings_low ?? 0;
        const cannibHigh = estimate?.cannibalization?.total_lost_savings_high ?? 0;
        onEstimateChange({
            estimate,
            participationMode,
            aggregatorCutPct,
            adjTotalAnnualLow: (grossLow * revFactor) - cannibLow,
            adjTotalAnnualHigh: (grossHigh * revFactor) - cannibHigh,
        });
    }, [estimate, participationMode, aggregatorCutPct, onEstimateChange]);

    // Theme-aware colors
    const textPrimary = isRetro ? vortxColors.white : vortxColors.charcoal;
    const textSecondary = isRetro ? vortxColors.stone : '#666';
    const cardBg = isRetro ? '#1a1a1a' : '#ffffff';
    const paperBg = isRetro ? '#242424' : '#ffffff';
    const borderColor = isRetro ? vortxColors.darkGreen : 'rgba(63, 106, 100, 0.2)';
    const chipEligibleBg = isRetro ? 'rgba(46, 125, 50, 0.3)' : 'rgba(46, 125, 50, 0.1)';
    const chipIneligibleBg = isRetro ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

    // --- Loading state ---
    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                <CircularProgress sx={{ color: vortxColors.gold, mb: 2 }} />
                <Typography variant="body1" sx={{ color: textSecondary }}>
                    Estimating VPP revenue...
                </Typography>
            </Box>
        );
    }

    // --- Error state ---
    if (error) {
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    // --- No data yet ---
    if (!estimate) return null;

    // --- Empty / no profile match ---
    if (!estimate.iso) {
        return (
            <Box sx={{ py: 4 }}>
                <Alert severity="info">
                    No wholesale market data available for this location. VPP revenue estimation
                    requires a mapped ISO zone.
                </Alert>
            </Box>
        );
    }

    // Participation factor: direct = 100%, aggregator = (100% - cut%)
    const revenueFactor = participationMode === 'direct' ? 1.0 : (100 - aggregatorCutPct) / 100;

    // Gross VPP revenue (before cannibalization, after aggregator fee)
    const adjGrossLow = estimate.total_annual_low * revenueFactor;
    const adjGrossHigh = estimate.total_annual_high * revenueFactor;

    // Cannibalization is a retail-side loss — full amount regardless of aggregator fee
    const cannibalization = estimate.cannibalization;
    const cannibLow = cannibalization?.total_lost_savings_low ?? 0;
    const cannibHigh = cannibalization?.total_lost_savings_high ?? 0;
    const hasCannib = cannibLow > 0 || cannibHigh > 0;

    // Net VPP revenue = (gross × aggregator factor) - cannibalization (full)
    const netLow = adjGrossLow - cannibLow;
    const netHigh = adjGrossHigh - cannibHigh;

    const hasRevenue = netLow > 0 || netHigh > 0;
    const existingSavings = estimate.existing_retail_savings || 0;
    const adjWithVppLow = existingSavings + netLow;
    const adjWithVppHigh = existingSavings + netHigh;

    // Compute combined uplift percentages
    let upliftLowPct = 0;
    let upliftHighPct = 0;
    if (existingSavings > 0 && hasRevenue) {
        upliftLowPct = (netLow / existingSavings) * 100;
        upliftHighPct = (netHigh / existingSavings) * 100;
    }

    return (
        <Box>
            {/* --- Header: ISO chip + profile name + freshness --- */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                <Chip
                    label={`${estimate.iso} \u2014 ${estimate.zone || 'Zone'}`}
                    size="small"
                    sx={{
                        backgroundColor: isRetro ? 'rgba(63, 106, 100, 0.4)' : vortxColors.teal,
                        color: '#fff',
                        fontWeight: 600,
                        fontFamily: vortxTypography.mono,
                    }}
                />
                <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: textPrimary }}
                >
                    {estimate.profile_name || 'Market Profile'}
                </Typography>
                {estimate.profile_updated_at && (
                    <Typography variant="caption" sx={{ color: textSecondary }}>
                        Data updated: {formatDate(estimate.profile_updated_at)}
                    </Typography>
                )}
            </Box>

            {/* --- Participation Mode Toggle --- */}
            <Paper
                sx={{
                    p: 2.5,
                    mb: 3,
                    backgroundColor: paperBg,
                    border: `1px solid ${borderColor}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: textPrimary, minWidth: 'fit-content' }}>
                        Participation:
                    </Typography>
                    <ToggleButtonGroup
                        value={participationMode}
                        exclusive
                        onChange={(e, val) => val && setParticipationMode(val)}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                px: 2,
                                py: 0.5,
                                color: textSecondary,
                                borderColor: borderColor,
                                '&.Mui-selected': {
                                    backgroundColor: isRetro ? 'rgba(63, 106, 100, 0.3)' : 'rgba(63, 106, 100, 0.1)',
                                    color: vortxColors.teal,
                                    fontWeight: 600,
                                    borderColor: vortxColors.teal,
                                },
                            },
                        }}
                    >
                        <ToggleButton value="direct">Self-Managed (100%)</ToggleButton>
                        <ToggleButton value="aggregator">Third-Party Aggregator</ToggleButton>
                    </ToggleButtonGroup>
                    {participationMode === 'aggregator' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 200 }}>
                            <Typography variant="caption" sx={{ color: textSecondary, whiteSpace: 'nowrap' }}>
                                Aggregator fee:
                            </Typography>
                            <Slider
                                value={aggregatorCutPct}
                                onChange={(e, val) => setAggregatorCutPct(val)}
                                min={5}
                                max={40}
                                step={5}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `${v}%`}
                                sx={{
                                    color: vortxColors.teal,
                                    maxWidth: 180,
                                    '& .MuiSlider-thumb': { width: 14, height: 14 },
                                }}
                            />
                            <Typography variant="caption" sx={{ color: textPrimary, fontWeight: 600, whiteSpace: 'nowrap', minWidth: 70 }}>
                                You keep {100 - aggregatorCutPct}%
                            </Typography>
                        </Box>
                    )}
                </Box>
                {participationMode === 'aggregator' && (
                    <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mt: 1 }}>
                        Third-party handles enrollment, bidding, compliance, and metering. Typical fees: 15-25%.
                    </Typography>
                )}
                {participationMode === 'direct' && (
                    <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mt: 1 }}>
                        Vortx acts as aggregator — full 100% incentive. Requires utility enrollment and telemetry.
                    </Typography>
                )}
            </Paper>

            {/* --- Revenue Summary --- */}
            <Paper
                sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: paperBg,
                    border: `1px solid ${borderColor}`,
                    borderLeft: `4px solid ${vortxColors.gold}`,
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700, color: vortxColors.gold, mb: 0.5 }}>
                    Net VPP Revenue: {fmtRange(netLow, netHigh)}/year
                </Typography>
                {hasCannib && (
                    <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>
                        Gross {fmtRange(adjGrossLow, adjGrossHigh)} less {fmtRange(cannibLow, cannibHigh)} opportunity cost
                    </Typography>
                )}
                {existingSavings > 0 && (
                    <Typography variant="body1" sx={{ color: textPrimary }}>
                        Combined with retail optimization:{' '}
                        <strong>
                            {fmtRange(adjWithVppLow, adjWithVppHigh)}/year
                        </strong>
                        {upliftHighPct > 0 && (
                            <span style={{ color: vortxColors.success, marginLeft: 8 }}>
                                (+{upliftLowPct.toFixed(0)}% to +{upliftHighPct.toFixed(0)}%)
                            </span>
                        )}
                    </Typography>
                )}
            </Paper>

            {/* --- Revenue Breakdown Table --- */}
            <Typography variant="h6" sx={{ fontWeight: 600, color: textPrimary, mb: 1.5 }}>
                Revenue Breakdown
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: paperBg, border: `1px solid ${borderColor}` }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Revenue Stream</TableCell>
                            <TableCell align="right">Annual Estimate</TableCell>
                            <TableCell>Basis</TableCell>
                            <TableCell>Notes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Existing retail row (if applicable) */}
                        {existingSavings > 0 && (
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    Retail Optimization (current)
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 500 }}>
                                    {fmtCurrency.format(existingSavings)}
                                </TableCell>
                                <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                                    Demand charge + TOU
                                </TableCell>
                                <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                                    Already captured
                                </TableCell>
                            </TableRow>
                        )}

                        {/* Revenue streams from API */}
                        {estimate.revenue_streams.map((stream, idx) => (
                            <TableRow key={idx}>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    {stream.name}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 500 }}>
                                    {stream.annual_low === 0 && stream.annual_high === 0
                                        ? '\u2014'
                                        : fmtRange(stream.annual_low * revenueFactor, stream.annual_high * revenueFactor)
                                    }
                                </TableCell>
                                <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                                    {stream.basis}
                                </TableCell>
                                <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                                    {stream.notes}
                                </TableCell>
                            </TableRow>
                        ))}

                        {/* DR program revenue (eligible only) */}
                        {estimate.dr_programs
                            .filter((p) => p.eligible && p.annual_est > 0)
                            .map((program, idx) => (
                                <TableRow key={`dr-${idx}`}>
                                    <TableCell sx={{ fontWeight: 500 }}>
                                        DR: {program.name}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                                        {fmtCurrency.format(program.annual_est * revenueFactor)}
                                    </TableCell>
                                    <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                                        {program.utility}
                                    </TableCell>
                                    <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                                        {program.eligible_months.length > 0
                                            ? `${program.eligible_months.join(', ')}`
                                            : 'Year-round'}
                                    </TableCell>
                                </TableRow>
                            ))
                        }

                        {/* Opportunity Cost deduction row */}
                        {hasCannib && (
                            <TableRow>
                                <TableCell sx={{ fontWeight: 500, color: vortxColors.error }}>
                                    Opportunity Cost (cannibalization)
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 500, color: vortxColors.error }}>
                                    ({fmtRange(cannibLow, cannibHigh)})
                                </TableCell>
                                <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                                    Lost peak-shaving during VPP dispatch
                                </TableCell>
                                <TableCell sx={{ color: textSecondary, fontSize: '0.8rem' }}>
                                    Battery can&apos;t peak-shave while dispatched
                                </TableCell>
                            </TableRow>
                        )}

                        {/* Totals row */}
                        <TableRow sx={{ '& td': { borderTop: `2px solid ${vortxColors.gold}`, fontWeight: 700 } }}>
                            <TableCell sx={{ fontWeight: 700 }}>
                                Total with VPP
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: vortxColors.gold }}>
                                {fmtRange(adjWithVppLow, adjWithVppHigh)}
                            </TableCell>
                            <TableCell />
                            <TableCell />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* --- Calculation Methodology (collapsible) --- */}
            <Box sx={{ mb: 3 }}>
                <Button
                    onClick={() => setShowCalcDetails(!showCalcDetails)}
                    startIcon={<CalculateIcon sx={{ fontSize: '1rem' }} />}
                    endIcon={showCalcDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    size="small"
                    sx={{
                        textTransform: 'none',
                        color: textSecondary,
                        fontWeight: 500,
                        mb: 1,
                        '&:hover': { color: vortxColors.teal, backgroundColor: 'transparent' },
                    }}
                >
                    {showCalcDetails ? 'Hide Calculation Details' : 'Show Calculation Details'}
                </Button>
                <Collapse in={showCalcDetails}>
                    <Paper sx={{
                        p: 2.5,
                        backgroundColor: isRetro ? '#1a1a1a' : '#fafafa',
                        border: `1px solid ${borderColor}`,
                    }}>
                        {/* Site Inputs */}
                        {estimate.site_inputs && estimate.site_inputs.length > 0 && (
                            <Box sx={{ mb: 2.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textPrimary, mb: 1 }}>
                                    Site Inputs
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                    {estimate.site_inputs.map((input, idx) => (
                                        <Chip
                                            key={idx}
                                            label={`${input.label}: ${input.value}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                borderColor: borderColor,
                                                color: textPrimary,
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Per-stream calculation details */}
                        {estimate.revenue_streams
                            .filter(s => s.calc_details)
                            .map((stream, idx) => (
                                <Box key={idx} sx={{ mb: idx < estimate.revenue_streams.filter(s => s.calc_details).length - 1 ? 2.5 : 0 }}>
                                    {idx > 0 && <Divider sx={{ mb: 2, borderColor: borderColor }} />}
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: vortxColors.teal, mb: 0.5 }}>
                                        {stream.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        color: textSecondary,
                                        fontFamily: vortxTypography.mono,
                                        display: 'block',
                                        mb: 1,
                                    }}>
                                        {stream.calc_details.formula}
                                    </Typography>

                                    {/* Input values table */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 16px', mb: 1.5 }}>
                                        {stream.calc_details.inputs.map((input, iIdx) => (
                                            <React.Fragment key={iIdx}>
                                                <Typography variant="caption" sx={{ color: textSecondary }}>
                                                    {input.label}:
                                                </Typography>
                                                <Typography variant="caption" sx={{
                                                    color: textPrimary,
                                                    fontFamily: vortxTypography.mono,
                                                    fontWeight: 500,
                                                }}>
                                                    {input.value}
                                                </Typography>
                                            </React.Fragment>
                                        ))}
                                    </Box>

                                    {/* Calculation steps */}
                                    {stream.calc_details.steps.map((step, sIdx) => (
                                        <Typography key={sIdx} variant="caption" sx={{
                                            display: 'block',
                                            color: textPrimary,
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.7rem',
                                            lineHeight: 1.8,
                                            pl: 1,
                                            borderLeft: `2px solid ${vortxColors.gold}`,
                                            mb: sIdx < stream.calc_details.steps.length - 1 ? 0.5 : 0,
                                        }}>
                                            {step}
                                        </Typography>
                                    ))}
                                </Box>
                            ))
                        }

                        {/* DR program calculation details */}
                        {estimate.dr_programs
                            .filter(p => p.calc_details)
                            .map((program, idx) => (
                                <Box key={`dr-calc-${idx}`} sx={{ mt: 2.5 }}>
                                    <Divider sx={{ mb: 2, borderColor: borderColor }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: vortxColors.teal, mb: 0.5 }}>
                                        DR: {program.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        color: textSecondary,
                                        fontFamily: vortxTypography.mono,
                                        display: 'block',
                                        mb: 1,
                                    }}>
                                        {program.calc_details.formula}
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 16px', mb: 1.5 }}>
                                        {program.calc_details.inputs.map((input, iIdx) => (
                                            <React.Fragment key={iIdx}>
                                                <Typography variant="caption" sx={{ color: textSecondary }}>
                                                    {input.label}:
                                                </Typography>
                                                <Typography variant="caption" sx={{
                                                    color: textPrimary,
                                                    fontFamily: vortxTypography.mono,
                                                    fontWeight: 500,
                                                }}>
                                                    {input.value}
                                                </Typography>
                                            </React.Fragment>
                                        ))}
                                    </Box>
                                    {program.calc_details.steps.map((step, sIdx) => (
                                        <Typography key={sIdx} variant="caption" sx={{
                                            display: 'block',
                                            color: textPrimary,
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.7rem',
                                            lineHeight: 1.8,
                                            pl: 1,
                                            borderLeft: `2px solid ${vortxColors.gold}`,
                                            mb: sIdx < program.calc_details.steps.length - 1 ? 0.5 : 0,
                                        }}>
                                            {step}
                                        </Typography>
                                    ))}
                                </Box>
                            ))
                        }

                        {/* Cannibalization breakdown */}
                        {cannibalization?.per_stream?.length > 0 && (
                            <Box sx={{ mt: 2.5 }}>
                                <Divider sx={{ mb: 2, borderColor: borderColor }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: vortxColors.error, mb: 0.5 }}>
                                    Opportunity Cost Breakdown
                                </Typography>
                                <Typography variant="caption" sx={{
                                    color: textSecondary,
                                    fontFamily: vortxTypography.mono,
                                    display: 'block',
                                    mb: 1,
                                }}>
                                    lost_savings = dispatch_hours_in_peak &times; overlap_factor &times; (existing_savings / peak_hours_per_year)
                                </Typography>
                                {cannibalization.per_stream.map((cs, idx) => (
                                    <Box key={idx} sx={{ mb: 1.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: textPrimary, display: 'block', mb: 0.25 }}>
                                            {cs.name} (overlap: {(cs.overlap_factor * 100).toFixed(0)}%)
                                        </Typography>
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            color: textPrimary,
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.7rem',
                                            lineHeight: 1.8,
                                            pl: 1,
                                            borderLeft: `2px solid ${vortxColors.error}`,
                                        }}>
                                            {cs.dispatch_hours_low === cs.dispatch_hours_high
                                                ? `${cs.dispatch_hours_low.toLocaleString()} dispatch hrs`
                                                : `${cs.dispatch_hours_low.toLocaleString()}\u2013${cs.dispatch_hours_high.toLocaleString()} dispatch hrs`
                                            } &times; {(cs.overlap_factor * 100).toFixed(0)}% overlap = {fmtRange(cs.lost_savings_low, cs.lost_savings_high)} lost savings
                                        </Typography>
                                    </Box>
                                ))}
                                <Typography variant="caption" sx={{
                                    display: 'block',
                                    color: vortxColors.error,
                                    fontFamily: vortxTypography.mono,
                                    fontWeight: 600,
                                    mt: 1,
                                }}>
                                    Total opportunity cost: {fmtRange(cannibLow, cannibHigh)}/yr
                                </Typography>
                            </Box>
                        )}

                        {/* Assumptions summary */}
                        {estimate.assumptions && (
                            <Box sx={{ mt: 2.5 }}>
                                <Divider sx={{ mb: 2, borderColor: borderColor }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textPrimary, mb: 1 }}>
                                    Key Assumptions
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 16px' }}>
                                    {[
                                        ['EV availability', estimate.assumptions.ev_availability_factor],
                                        ['ELCC factor', estimate.assumptions.elcc_factor],
                                        ['Regulation hours/year', estimate.assumptions.reg_eligible_hours],
                                        ['Regulation performance', `${estimate.assumptions.reg_performance_factor * 100}%`],
                                        ['DR event duration', `${estimate.assumptions.dr_event_hours} hours`],
                                        ['Peak hours/year', estimate.assumptions.peak_hours_per_year ? `${estimate.assumptions.peak_hours_per_year.toLocaleString()}` : null],
                                        ...(estimate.assumptions.cannibalization_overlap
                                            ? Object.entries(estimate.assumptions.cannibalization_overlap).map(
                                                ([stream, pct]) => [`Cannib. overlap: ${stream}`, pct]
                                            )
                                            : []),
                                    ].filter(([, v]) => v != null).map(([label, value], idx) => (
                                        <React.Fragment key={idx}>
                                            <Typography variant="caption" sx={{ color: textSecondary }}>
                                                {label}:
                                            </Typography>
                                            <Typography variant="caption" sx={{
                                                color: textPrimary,
                                                fontFamily: vortxTypography.mono,
                                                fontWeight: 500,
                                            }}>
                                                {value}
                                            </Typography>
                                        </React.Fragment>
                                    ))}
                                </Box>
                                <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mt: 1.5, fontStyle: 'italic' }}>
                                    Low/high ranges reflect conservative vs. optimistic scenarios. Market prices based on rolling 52-week averages.
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Collapse>
            </Box>

            {/* --- DR Program Eligibility Cards --- */}
            {estimate.dr_programs.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: textPrimary, mb: 1.5 }}>
                        Demand Response Programs
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        {estimate.dr_programs.map((program, idx) => (
                            <Card
                                key={idx}
                                sx={{
                                    flex: '1 1 280px',
                                    maxWidth: 400,
                                    backgroundColor: cardBg,
                                    border: `1px solid ${program.eligible ? vortxColors.success : borderColor}`,
                                }}
                            >
                                <CardContent sx={{ pb: '16px !important' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textPrimary, flex: 1 }}>
                                            {program.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                            {program.tariff_url && (
                                                <Tooltip title="View tariff schedule" arrow>
                                                    <IconButton
                                                        size="small"
                                                        component="a"
                                                        href={program.tariff_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ color: textSecondary, '&:hover': { color: vortxColors.teal }, p: 0.25 }}
                                                    >
                                                        <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Chip
                                                label={program.eligible ? 'Eligible' : 'Ineligible'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: program.eligible ? chipEligibleBg : chipIneligibleBg,
                                                    color: program.eligible ? vortxColors.success : textSecondary,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    {program.utility && (
                                        <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mb: 0.5 }}>
                                            {program.utility}
                                        </Typography>
                                    )}
                                    {program.eligible && program.annual_est > 0 && (
                                        <Typography variant="body2" sx={{ color: vortxColors.gold, fontWeight: 600, mb: 0.5 }}>
                                            {fmtCurrency.format(program.annual_est * revenueFactor)}/year
                                        </Typography>
                                    )}
                                    {program.eligible_months.length > 0 && (
                                        <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mb: 0.5 }}>
                                            Active months: {program.eligible_months.join(', ')}
                                        </Typography>
                                    )}
                                    {program.min_commitment_kw > 0 && (
                                        <Typography variant="caption" sx={{ color: textSecondary, display: 'block' }}>
                                            Min. commitment: {program.min_commitment_kw} kW
                                        </Typography>
                                    )}
                                    {!program.eligible && program.reason && (
                                        <Typography variant="caption" sx={{ color: vortxColors.error, display: 'block', mt: 0.5 }}>
                                            {program.reason}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </>
            )}

            {/* --- Footer disclaimer --- */}
            <Divider sx={{ mb: 2, borderColor: borderColor }} />
            <Typography variant="caption" sx={{ color: textSecondary, lineHeight: 1.6 }}>
                Estimates based on rolling 52-week market averages. Actual revenue depends on dispatch
                optimization, market conditions, and program enrollment.
                {estimate.profile_updated_at && (
                    <> Market data last updated: {formatDate(estimate.profile_updated_at)}.</>
                )}
            </Typography>
        </Box>
    );
}

/**
 * Format an ISO date string or Firestore timestamp into a readable date.
 */
function formatDate(dateStr) {
    if (!dateStr || dateStr === 'None') return 'Unknown';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return dateStr;
    }
}
